import type { AxiosInstance } from "axios";
import type { TokenManager } from "../../auth/token-manager";
import { isAbsoluteUrl } from "./is-absolute-url";

/**
 * Bearer-token injection from the ONE TokenManager the app owns.
 *
 * Differences from the dashboard's auth.ts, both deliberate:
 * - no tokenService.setAccessToken — the editor token is memory-only, it
 *   never touches localStorage;
 * - no silent-failure fallthrough — a token we cannot get is a failed
 *   request (typed, immediate), not a tokenless call that burns a network
 *   round-trip to collect an inevitable 401.
 *
 * The manager is an explicit argument, not the dashboard's module-level
 * setTokenGetter: that getter is shared mutable state the dashboard's own
 * interceptors read, and setting it while both apps share this worktree
 * would break every dashboard request.
 */
export function installStudioAuth(
  instance: AxiosInstance,
  manager: TokenManager,
): number {
  return instance.interceptors.request.use(async (config) => {
    // The editor token goes only to our API — never to third-party hosts
    // (Azure SAS etc.). Checked before getToken() so an absolute-URL request
    // never mints or waits on a token round-trip it has no use for.
    if (isAbsoluteUrl(config.url ?? "")) return config;
    const token = await manager.getToken();
    config.headers["Authorization"] = `Bearer ${token}`;
    return config;
  });
}
